import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useEffect } from "react";
import { Button } from "@shadcn/components/ui/button";
import { RepeaterProps } from "./Repeater.types";
import {
  ElementSchema,
  ElementType,
  InputTypes,
  Pipe,
  PipeType,
  StandardPipe,
  JunctionType,
  BranchJunction,
  BranchRule,
  QuestionElementSchema,
  IntegrationElementSchema,
  FieldType,
} from "@schema-types/types/JourneySchema";
import * as Schema from "@schema-types/types/JourneySchema";
import { ElementRenderer } from "../ElementRenderer";
import { Plus, Trash2 } from "lucide-react";
import { findIntegrationBranchProperty } from "@schema-types/integrationBranchProperties";
import { evaluateRule } from "../../Steps/FormStep/evaluateRule";
import { getBrowserIanaTimeZoneId } from "@schema-types/enquiryEvaluationTimeZone";

const resolveBranchSourceInElements = (
  elements: ElementSchema[],
  sourceElementId: string,
  sourceFieldId: string
): { dataKey: string; propertyPath?: string } | undefined => {
  const element = elements.find((e) => e.id === sourceElementId);
  if (!element) return undefined;

  if (element.type === ElementType.Question) {
    const question = element as QuestionElementSchema;
    const field = question.fields.find((f) => f.id === sourceFieldId);
    if (field && field.type === FieldType.Input) {
      return { dataKey: field.name };
    }
    return undefined;
  }

  if (element.type === ElementType.Integration) {
    const integration = element as IntegrationElementSchema;
    const property = findIntegrationBranchProperty(integration.integrationType, sourceFieldId);
    if (!property) return undefined;
    return { dataKey: integration.displayId, propertyPath: property.path };
  }

  return undefined;
};

const readResolvedValue = (
  data: Record<string, any> | undefined,
  resolved: { dataKey: string; propertyPath?: string }
): unknown => {
  if (!data) return undefined;
  const container = data[resolved.dataKey];
  if (!resolved.propertyPath) return container;
  if (container == null || typeof container !== "object") return undefined;
  return (container as Record<string, unknown>)[resolved.propertyPath];
};

const getNextPipeIdInRow = (
  currentPipe: Pipe,
  rowValues: Record<string, any>,
  elements: ElementSchema[],
  evaluationTimeZoneId: string
): string | undefined => {
  if (!currentPipe.junctions || currentPipe.junctions.length === 0) {
    return undefined;
  }

  const evaluate = (rule: BranchRule) => {
    const resolved = resolveBranchSourceInElements(elements, rule.sourceQuestionId, rule.sourceFieldId);
    if (!resolved) return false;
    const value = readResolvedValue(rowValues, resolved);
    return evaluateRule(
      value,
      rule.comparisonValue,
      rule.comparisonType,
      rule.comparisonValueType,
      evaluationTimeZoneId
    );
  };

  const branchJunctions = currentPipe.junctions.filter((link) => link.type === JunctionType.Branch) as BranchJunction[];
  for (const branch of branchJunctions) {
    if (branch.rules) {
      const matchingRule = branch.rules.find((rule) => evaluate(rule));
      if (matchingRule) {
        return branch.targetPipeId;
      }
    }
  }

  const directLink = currentPipe.junctions.find((link) => link.type === JunctionType.Direct);
  return directLink?.targetPipeId;
};

const walkVisibleElements = (
  pipeline: Pipe[],
  elements: ElementSchema[],
  rowValues: Record<string, any>,
  evaluationTimeZoneId: string
): ElementSchema[] => {
  const visible: ElementSchema[] = [];
  if (!pipeline.length) return visible;

  let currentPipe: Pipe | undefined = pipeline[0];
  if (currentPipe?.type === PipeType.Start) {
    const nextId = getNextPipeIdInRow(currentPipe, rowValues, elements, evaluationTimeZoneId);
    currentPipe = nextId ? pipeline.find((p) => p.id === nextId) : undefined;
  }

  while (currentPipe) {
    if (currentPipe.type === PipeType.ElementRepeater) {
      break;
    }
    if (currentPipe.type === PipeType.Standard) {
      const standard = currentPipe as StandardPipe;
      const element = elements.find((e) => e.id === standard.targetId);
      if (element) visible.push(element);
    }

    const nextId = getNextPipeIdInRow(currentPipe, rowValues, elements, evaluationTimeZoneId);
    if (!nextId) break;
    currentPipe = pipeline.find((p) => p.id === nextId);
  }

  return visible;
};

export const Repeater = ({
  displayId,
  title,
  elements,
  pipeline,
  minItems,
  maxItems,
  addButtonText,
  removeButtonText,
  hideElementTitles = false,
  disabled,
  formStepId,
  evaluationTimeZoneId,
}: RepeaterProps) => {
  const { control, formState, setError, clearErrors, trigger, getValues, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: displayId,
  });

  const allChildElements = (() => {
    const ids = new Set<string>();
    const list: ElementSchema[] = [];
    for (const pipe of pipeline ?? []) {
      if (pipe.type !== PipeType.Standard) continue;
      const id = (pipe as StandardPipe).targetId;
      if (!id || ids.has(id)) continue;
      ids.add(id);
      const el = elements.find((e) => e.id === id);
      if (el) list.push(el);
    }
    return list;
  })();

  const watchedRows = useWatch({ control, name: displayId }) as Record<string, any>[] | undefined;
  const tz = evaluationTimeZoneId || getBrowserIanaTimeZoneId();

  const getDefaultValues = () => {
    const defaults: Record<string, any> = {};
    allChildElements.forEach((element) => {
      if (element.type === ElementType.Question) {
        const questionElement = element as Schema.QuestionElementSchema;
        questionElement.fields.forEach((field) => {
          if (field.type === "Input" && "name" in field) {
            switch (field.inputType) {
              case InputTypes.Money:
              case InputTypes.Number:
                defaults[field.name] = undefined;
                break;
              case InputTypes.Date:
                defaults[field.name] = null;
                break;
              case InputTypes.Checkbox:
                defaults[field.name] = [];
                break;
              case InputTypes.FileUpload:
                defaults[field.name] = [];
                break;
              default:
                defaults[field.name] = "";
            }
          }
        });
      }
    });
    return defaults;
  };

  const addNewSet = () => {
    append(getDefaultValues());
    const currentValue = getValues(displayId);
    setValue(displayId, currentValue || [], { shouldTouch: true });
  };

  const removeSet = (index: number) => {
    remove(index);
    const currentValue = getValues(displayId);
    setValue(displayId, currentValue || [], { shouldTouch: true });
  };

  const canAdd = () => {
    if (disabled) return false;
    if (maxItems > 0 && fields.length >= maxItems) return false;
    return true;
  };

  const canRemove = () => {
    if (disabled) return false;
    return fields.length > minItems;
  };

  const renderElement = (element: ElementSchema, setIndex: number) => (
    <ElementRenderer
      key={element.id}
      element={element}
      formStepId={formStepId}
      namePrefix={`${displayId}.${setIndex}`}
      hideElementTitles={hideElementTitles}
      disabled={disabled}
    />
  );

  useEffect(() => {
    if (fields.length === 0) {
      append(getDefaultValues());
    }
  }, []);

  useEffect(() => {
    if (minItems > 0) {
      if (fields.length < minItems) {
        setError(displayId, {
          type: "minItems",
          message: `At least ${minItems} ${minItems === 1 ? "item is" : "items are"} required`,
        });
      } else {
        clearErrors(displayId);
      }
      trigger(displayId);
    }
  }, [fields.length, minItems, displayId, setError, clearErrors, trigger]);

  const minItemsError =
    formState.isSubmitted || formState.touchedFields[displayId]
      ? (formState.errors[displayId]?.message as string | undefined)
      : undefined;

  return (
    <div className="jb-repeater space-y-6">
      <h3 className="text-lg font-medium">{title}</h3>
      {minItemsError && <p className="text-sm font-medium text-destructive">{minItemsError}</p>}

      {fields.map((field, index) => {
        const rowValues = (watchedRows?.[index] as Record<string, any>) ?? {};
        const visibleElements = walkVisibleElements(pipeline ?? [], elements, rowValues, tz);

        return (
          <div key={field.id} className="relative space-y-4 rounded-lg border border-gray-200 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">
                {title} #{index + 1}
              </span>
              {canRemove() && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeSet(index)}
                  disabled={disabled}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  {removeButtonText}
                </Button>
              )}
            </div>

            <div className="space-y-4">{visibleElements.map((element) => renderElement(element, index))}</div>
          </div>
        );
      })}

      <Button type="button" variant="outline" onClick={addNewSet} disabled={!canAdd()} className="w-full">
        <Plus className="mr-2 h-4 w-4" />
        {addButtonText}
      </Button>
    </div>
  );
};
